import { Component, DestroyRef, Input, OnChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { Constants } from '../../../admin-core/constants';
import { PRODUCT_CARD_RETRY_LATER_MS, ProductCard, ProductCardsService } from '../../product-cards.service';

/**
 * One product's entry in a Pebbl reply: its picture on the left, the entry's own reply text
 * — projected, so it keeps the chat's Markdown styling — on the right. ChatViewComponent
 * renders one per entry that placeProductCards() finds: the entry takes the `product_cards` SKU
 * whose `product_link` it links to, or — when the reply carries none — the product its name
 * links to.
 *
 * The picture is decorative beside a name that is already a link, so it is hidden from
 * assistive tech and out of the tab order. A placeholder holds its final size while the SKU
 * resolves, so the reply does not jump; a SKU that does not resolve leaves the entry as text
 * alone. A lookup that FAILED (rather than finding no product) is asked again on
 * PRODUCT_CARD_RETRY_LATER_MS, so a network blip does not leave the entry blank for good. The SKU itself is never rendered: not as text, alt, title or aria-label. Styles live
 * in ai.styles.css (`.pe-*`), which the two hosts load unencapsulated.
 */
@Component({
  selector: 'app-ai-product-entry',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './product-entry.component.html',
  host: {
    '[class.pe--divided]': 'divided',
    '[class.pe--no-picture]': 'state() === "missing"',
  },
})
export class ProductEntryComponent implements OnChanges {
  /* ################### Variable Declaration Start ################### */
  /** The reply's whole SKU list — every entry asks with it, so they share one lookup. */
  @Input() skus: string[] = [];
  /** Which SKU's picture this entry takes — the card whose `product_link` the entry links to;
   *  null where none matches, which leaves the entry as text alone. */
  @Input() index: number | null = 0;
  /** With no SKUs: the storefront link on the entry's name, which the picture is looked up by. */
  @Input() link: string | null = null;
  /** The entry's product name, the fallback lookup when the link's slug does not match. */
  @Input() name = '';
  /** Directly under another entry: draws the divider between the two. */
  @Input() divided = false;

  card = signal<ProductCard | null>(null);
  state = signal<'loading' | 'ready' | 'missing'>('loading');

  /** The SKU list and index last resolved — a re-render per token must not fetch again. */
  private resolvedKey: string | null = null;
  private subscription?: Subscription;
  /** How many times the current lookup has been asked again after failing. */
  private retries = 0;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private readonly destroyRef = inject(DestroyRef);
  /* ################### Variable Declaration End ################### */

  constructor(private productCards: ProductCardsService) {
    this.destroyRef.onDestroy(() => clearTimeout(this.retryTimer));
  }

  ngOnChanges(): void {
    const key = JSON.stringify([this.index, this.skus ?? [], this.link]);
    if (key === this.resolvedKey) return;
    this.resolvedKey = key;
    this.retries = 0;
    this.load();
  }

  /** Look the picture up (again, on a retry) for the current inputs. */
  private load(): void {
    this.subscription?.unsubscribe();
    clearTimeout(this.retryTimer);
    const skus = this.skus ?? [];

    const index = this.index;
    // No frame, or an entry the frame has no card for: look the product up by its own link.
    if (this.link && (!skus.length || index === null || !skus[index])) {
      this.resolveByLink(this.link);
      return;
    }
    if (index === null || !skus[index]) {
      this.apply(null);
      return;
    }
    // Already resolved (prefetched on the frame, chat switch, widget reopen): no placeholder flash.
    const cached = this.productCards.cachedEach(skus);
    if (cached) {
      this.apply(cached[index]);
      return;
    }
    this.showLoading();
    const sku = skus[index];
    this.subscription = this.productCards
      .resolveEach(skus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cards) => {
        this.apply(cards[index]);
        if (!this.productCards.isSettled(sku)) this.retryLater();
      });
  }

  private resolveByLink(link: string): void {
    const cached = this.productCards.cachedLink(link);
    if (cached !== undefined) {
      this.apply(cached);
      return;
    }
    this.showLoading();
    this.subscription = this.productCards
      .resolveLink(link, this.name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((card) => {
        this.apply(card);
        if (this.productCards.cachedLink(link) === undefined) this.retryLater();
      });
  }

  /** The placeholder on a first lookup only — a background retry keeps the entry as text. */
  private showLoading(): void {
    if (this.retries === 0) this.state.set('loading');
  }

  /** The lookup failed: ask again on the next PRODUCT_CARD_RETRY_LATER_MS step, if any is left. */
  private retryLater(): void {
    const delay = PRODUCT_CARD_RETRY_LATER_MS[this.retries];
    if (delay === undefined) return;
    this.retryTimer = setTimeout(() => {
      this.retries++;
      this.load();
    }, delay);
  }

  /** Swap a broken hero image for the shared placeholder (once — no error loop). */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.src !== Constants.NO_IMAGE_PRODUCT) img.src = Constants.NO_IMAGE_PRODUCT;
  }

  private apply(card: ProductCard | null | undefined): void {
    this.card.set(card ?? null);
    this.state.set(card ? 'ready' : 'missing');
  }
}
